import { useRefGetter, useThrottle, useWindowEvent } from '@eturi/react'
import { accountId$, isAuthenticated$ } from '@op/services'
import { setTag, setUser } from '@sentry/react'
import cls from 'classnames'
import { lazy, memo, useEffect, useLayoutEffect } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { identify } from './analytics'
import { AppPages } from './AppPages'
import { Footer } from './components'
import { Ably, Chat, GuardModal, ProductModal } from './components/lazy'
import { isMenuView$ } from './compound-selectors/app-misc'
import { currentLang$ } from './compound-selectors/language'
import { shouldShowFooter$ } from './compound-selectors/routes'
import { ZAppContent, ZOverlayAppSibling } from './constants/z-index'
import { topNavLG, topNavSM } from './css-variables'
import { useResponsiveMatches } from './hooks'
import { isTrialBannerVisible$, showMobileManageTabs$ } from './reducers/app-misc.slice'
import { LoadEmpty, NavSlideBox, Overlay } from './widgets'

const EmailVerificationModal = lazy(
	() => import('./components/EmailVerification/EmailVerificationModal'),
)

const PairingModal = lazy(() => import('./components/PairDevice/PairingModal'))

const AppMain = () => {
	const [getAppHeight, setAppHeight] = useRefGetter(0)
	const accountId = useSelector(accountId$)
	const canShowFooter = useSelector(shouldShowFooter$)
	const currentLang = useSelector(currentLang$)
	const isAuthenticated = useSelector(isAuthenticated$)
	const isTrialBannerVisible = useSelector(isTrialBannerVisible$)
	const { pathname } = useLocation()
	const isOnboarding = pathname === '/onboarding'
	const isMenuView = useSelector(isMenuView$)
	const responsiveMatches = useResponsiveMatches()
	const showMobileMenuTabs = useSelector(showMobileManageTabs$)

	// NOTE: If we need to add an iOS discriminator class this is what we'll use.
	//  That code was removed due to refactoring the layouts.
	// const isSupportedIOS = useSelector(isSupportedIOS$);

	const setViewHeight = useThrottle(
		() => {
			const appHeight = window.innerHeight

			if (appHeight === getAppHeight()) return

			document.documentElement.style.setProperty('--vh', `${appHeight / 100}px`)
			setAppHeight(appHeight)
		},
		72,
		{ leading: true, trailing: true },
	)

	useWindowEvent('resize', setViewHeight)
	useWindowEvent('orientationchange', setViewHeight)

	// Set the view height for our ghetto layout
	useLayoutEffect(setViewHeight, [])

	useEffect(() => {
		window.OURPACT.version = process.env.APP_VERSION
	}, [])

	// Set or clear Sentry user based on account id.
	useEffect(() => {
		setUser(accountId ? { id: accountId } : null)
		if (accountId) identify(accountId)
	}, [accountId])

	// Set a tag for the current language
	useEffect(() => {
		setTag('lang', currentLang)
	}, [currentLang])

	return (
		<>
			<NavSlideBox
				className={cls(
					'op-app-content',
					showMobileMenuTabs && 'has-mobile-tabs',
					isTrialBannerVisible && 'has-trial-banner',
				)}
				id="op-app-content"
				style={{
					paddingTop:
						isMenuView ?
							responsiveMatches.lg ?
								topNavLG
							:	topNavSM
						:	undefined,
					zIndex: ZAppContent,
				}}
			>
				<AppPages />
				{isAuthenticated && (
					<>
						{!isOnboarding && (
							<LoadEmpty>
								<Ably />
							</LoadEmpty>
						)}

						<LoadEmpty>
							<EmailVerificationModal />
						</LoadEmpty>

						<LoadEmpty>
							<GuardModal />
						</LoadEmpty>

						<LoadEmpty>
							<ProductModal />
						</LoadEmpty>
						<LoadEmpty>
							<PairingModal />
						</LoadEmpty>
					</>
				)}

				{canShowFooter && <Footer />}

				<Overlay />
			</NavSlideBox>

			{!responsiveMatches.lg && <Overlay zIndex={ZOverlayAppSibling} />}

			<LoadEmpty>
				<Chat />
			</LoadEmpty>
		</>
	)
}

export default /*@__PURE__*/ memo(AppMain)
